import { CourtItem, CourtSlot } from "../types/userProfile";
import { getSystemSettings } from "./commissionConfig";
import { resolveArenaFinancialModelSync } from "../services/financial/FinancialModelResolver";
import {
  resolveCommercialRule,
  loadCommercialCourtesies,
  saveCommercialCourtesy,
  toggleCommercialCourtesy,
  deleteCommercialCourtesy,
  loadCommercialAuditLogs,
  addCommercialAuditLog,
  createCommercialOperationSnapshot,
  COMMERCIAL_RULE_VERSION,
} from "./commercialHierarchyEngine";
export {
  resolveCommercialRule,
  loadCommercialCourtesies,
  saveCommercialCourtesy,
  toggleCommercialCourtesy,
  deleteCommercialCourtesy,
  loadCommercialAuditLogs,
  addCommercialAuditLog,
  createCommercialOperationSnapshot,
  COMMERCIAL_RULE_VERSION,
};

export interface MonthlyAccumulatedSummary {
  periodStart: string;
  periodEnd: string;
  nextClosingDate: string;
  totalEligibleBookingsCount: number;
  totalRevenueAmount: number;
  totalAccumulatedCommission: number;
  totalCancelledExemptBookingsCount: number;
  status: "EM ABERTO" | "PENDENTE" | "PAGO";
  detailsByCourt: {
    courtId: string;
    courtName: string;
    commissionRate: number;
    eligibleBookingsCount: number;
    revenueAmount: number;
    commissionAmount: number;
  }[];
}

export const ALLOWED_COURT_COMMISSION_RATES = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

/**
 * Valida se a taxa de comissão customizada de uma quadra está dentro da escala oficial (10% a 100% de 10 em 10) ou nula (padrão).
 */
export function validateCustomCommissionRate(rate: any): { valid: boolean; normalizedRate?: number; error?: string } {
  if (rate === undefined || rate === null || rate === "") {
    return { valid: true, normalizedRate: undefined };
  }
  const numRate = Number(rate);
  if (isNaN(numRate)) {
    return { valid: false, error: "A comissão da quadra deve ser um número válido." };
  }
  if (!ALLOWED_COURT_COMMISSION_RATES.includes(numRate)) {
    return {
      valid: false,
      error: `Comissão da quadra inválida (${numRate}%). Escolha um valor entre 10% e 100% em passos de 10%, ou deixe em branco para usar o padrão da plataforma.`,
    };
  }
  return { valid: true, normalizedRate: numRate };
}

/**
 * Retorna a taxa de comissão efetiva de uma quadra conforme a governança hierárquica:
 * 1. CORTESIA COMERCIAL (se ativa e na validade)
 * 2. customCommissionRate da quadra (se definido)
 * 3. comissão da arena (se houver)
 * 4. comissão padrão da plataforma (platformCommissionRate / courtCommissionRate)
 */
export function getEffectiveCourtCommissionRate(
  court?: Partial<CourtItem> | null,
  date?: string,
  arenaId?: string,
  arenaData?: any
): number {
  const courtesyResolved = resolveCommercialRule("BOOKING", { court, date });
  if (courtesyResolved.source === "COURTESY") {
    return courtesyResolved.effectiveRate;
  }

  const effectiveArenaId =
    arenaId ||
    court?.arenaId ||
    court?.ownerId ||
    (court as any)?.arenaOwnerId ||
    (court as any)?.ownerUid;

  const resolvedModel = resolveArenaFinancialModelSync({
    courtData: court as CourtItem,
    arenaId: effectiveArenaId,
    arenaData,
  });

  if (resolvedModel.isFixedMonthly || resolvedModel.model === "FIXED_MONTHLY") {
    return 0;
  }
  return Number(resolvedModel.commissionRate ?? courtesyResolved.effectiveRate);
}

/**
 * Versão assíncrona autoritativa que busca no Firestore se necessário.
 */
export async function getEffectiveCourtCommissionRateAsync(
  court?: Partial<CourtItem> | null,
  date?: string,
  arenaId?: string
): Promise<number> {
  const courtesyResolved = resolveCommercialRule("BOOKING", { court, date });
  if (courtesyResolved.source === "COURTESY") {
    return courtesyResolved.effectiveRate;
  }

  const effectiveArenaId =
    arenaId ||
    court?.arenaId ||
    court?.ownerId ||
    (court as any)?.arenaOwnerId ||
    (court as any)?.ownerUid;

  const { resolveArenaFinancialModel } = await import("../services/financial/FinancialModelResolver");
  const resolvedModel = await resolveArenaFinancialModel({
    courtData: court as CourtItem,
    arenaId: effectiveArenaId,
    forceRefresh: false,
  });

  if (resolvedModel.isFixedMonthly || resolvedModel.model === "FIXED_MONTHLY") {
    return 0;
  }
  return Number(resolvedModel.commissionRate ?? courtesyResolved.effectiveRate);
}

/**
 * Retorna o prazo de cancelamento da quadra em horas (2, 4 ou 6 horas - padrão 4h).
 */
export function getEffectiveCancellationWindowHours(court?: Partial<CourtItem> | null): number {
  if (court) {
    const hours = court.cancellationWindowHours || (court as any).cancellationHours;
    if (hours === 2 || hours === 4 || hours === 6) {
      return hours;
    }
  }
  return 4;
}

/**
 * Calcula o ISO timestamp para o limite de cancelamento (cancellationDeadlineAt).
 */
export function calculateCancellationDeadline(dateStr: string, timeStr: string, windowHours: number): string {
  try {
    const startMs = new Date(`${dateStr}T${timeStr}:00`).getTime();
    if (isNaN(startMs)) {
      return new Date().toISOString();
    }
    return new Date(startMs - windowHours * 3600 * 1000).toISOString();
  } catch {
    return new Date().toISOString();
  }
}

/**
 * Avalia se uma reserva é elegível para cobrança/comissão mensal:
 * - Reserva confirmada/marcada/concluída -> elegível.
 * - Reservas de atletas mensalistas (bookingType === 'mensalista') -> elegíveis (geram comissão normalmente).
 * - Criada dentro da janela de cancelamento (ex: criada a 3h do jogo quando prazo é 4h) -> elegível.
 * - Cancelada DEPOIS do prazo limite -> elegível.
 * - Cancelada ANTES ou NO prazo limite (e criada fora da janela) -> NÃO elegível (isenta).
 */
export function isBookingCommissionEligible(slot: CourtSlot | any): boolean {
  if (!slot) return false;

  // Se for um slot reservado para torneio (aluguel de quadra de torneio) -> EXCLUÍDO de elegibilidade para comissão da mensalidade
  if (slot.isTournamentSlot === true) {
    return false;
  }

  const isCancelled = Boolean(
    slot.status === "cancelada" ||
    slot.status === "cancelado" ||
    slot.paymentStatus === "cancelado" ||
    slot.isCancelled === true
  );

  // Se não estiver cancelada (status marcada, confirmada, concluida, etc.) -> ELEGÍVEL
  if (!isCancelled) {
    return true;
  }

  // Se estiver cancelada, verificar horários
  const bookingDateStr = slot.date;
  const bookingTimeStr = slot.time;
  const windowHours = slot.cancellationWindowHours || slot.cancellationHours || 4;

  let startMs = 0;
  if (bookingDateStr && bookingTimeStr) {
    startMs = new Date(`${bookingDateStr}T${bookingTimeStr}:00`).getTime();
  }

  if (!startMs || isNaN(startMs)) {
    return false; // Fallback seguro
  }

  const deadlineMs = slot.appliedCancellationDeadlineAt
    ? new Date(slot.appliedCancellationDeadlineAt).getTime()
    : startMs - windowHours * 3600 * 1000;

  const createdAtMs = slot.createdAt ? new Date(slot.createdAt).getTime() : startMs - (windowHours + 1) * 3600 * 1000;
  const cancelledAtMs = slot.cancelledAt ? new Date(slot.cancelledAt).getTime() : Date.now();

  // Regra 1: Foi criada dentro da janela de cancelamento? (createdAt >= deadline)
  if (createdAtMs >= deadlineMs) {
    return true; // Criada dentro da janela de 2/4/6h -> obrigatoriamente contabiliza
  }

  // Regra 2: Foi cancelada após o prazo limite? (cancelledAt > deadline)
  if (cancelledAtMs > deadlineMs) {
    return true; // Cancelamento tardio -> contabiliza
  }

  // Cancelada antes do prazo e criada antes da janela -> ISENTA
  return false;
}

/**
 * Calcula a comissão congelada da reserva.
 */
export function calculateSlotCommission(slot: CourtSlot | any, court?: Partial<CourtItem> | null): { rate: number; amount: number } {
  if (slot?.isTournamentSlot === true) {
    return { rate: 0, amount: 0 };
  }

  // Se o slot possui indicação explícita de modelo FIXED_MONTHLY ou pagamento por mensalidade fixa
  if (
    slot?.financialModelApplied === "FIXED_MONTHLY" ||
    slot?.financialModel === "FIXED_MONTHLY" ||
    slot?.paymentGateway === "mensalidade_fixa"
  ) {
    return { rate: 0, amount: 0 };
  }

  // Se o slot possui comissão congelada explicitamente no histórico
  if (slot?.commissionRate !== undefined && slot?.commissionRate !== null) {
    const rate = Number(slot.commissionRate);
    if (slot.appCommission !== undefined && slot.appCommission !== null) {
      return { rate, amount: Number(slot.appCommission) };
    }
    const price = Number(slot.price || slot.paidAmount || 0);
    const amount = Number(((price * rate) / 100).toFixed(2));
    return { rate, amount };
  }

  // Se não houver taxa congelada no slot, resolver via modelo efetivo da quadra/arena
  if (court) {
    const resolved = resolveArenaFinancialModelSync({ courtData: court as CourtItem });
    if (resolved.isFixedMonthly || resolved.model === "FIXED_MONTHLY") {
      return { rate: 0, amount: 0 };
    }
    const rate = resolved.commissionRate;
    const price = Number(slot?.price || slot?.paidAmount || 0);
    const amount = Number(((price * rate) / 100).toFixed(2));
    return { rate, amount };
  }

  const rate = getEffectiveCourtCommissionRate(court);
  if (slot?.appCommission !== undefined && slot?.appCommission !== null) {
    return { rate, amount: Number(slot.appCommission) };
  }
  const price = Number(slot?.price || slot?.paidAmount || 0);
  const amount = Number(((price * rate) / 100).toFixed(2));
  return { rate, amount };
}

/**
 * Calcula a acumulado do ciclo mensal em tempo real para as quadras da arena.
 */
export function calculateMonthlyBillingAccumulated(
  courts: CourtItem[],
  periodStart?: string,
  periodEnd?: string
): MonthlyAccumulatedSummary {
  const now = new Date();
  
  // Período padrão: 30 dias do ciclo
  const defaultStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
  const defaultEnd = now.toISOString().split("T")[0];

  const pStart = periodStart || defaultStart;
  const pEnd = periodEnd || defaultEnd;

  const nextClosingObj = new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000); // Ex: fechamento próximo
  const nextClosingDate = nextClosingObj.toISOString().split("T")[0];

  let totalEligibleBookingsCount = 0;
  let totalRevenueAmount = 0;
  let totalAccumulatedCommission = 0;
  let totalCancelledExemptBookingsCount = 0;

  const detailsMap = new Map<string, {
    courtId: string;
    courtName: string;
    commissionRate: number;
    eligibleBookingsCount: number;
    revenueAmount: number;
    commissionAmount: number;
  }>();

  courts.forEach((court) => {
    const resolvedCourtModel = resolveArenaFinancialModelSync({ courtData: court });
    const isCourtFixedMonthly = resolvedCourtModel.isFixedMonthly || resolvedCourtModel.model === "FIXED_MONTHLY";
    const courtRate = isCourtFixedMonthly ? 0 : (resolvedCourtModel.commissionRate || getEffectiveCourtCommissionRate(court));

    detailsMap.set(court.id, {
      courtId: court.id,
      courtName: court.courtName,
      commissionRate: courtRate,
      eligibleBookingsCount: 0,
      revenueAmount: 0,
      commissionAmount: 0,
    });

    const slots = court.slots || [];
    slots.forEach((slot) => {
      // Exclusão absoluta: Se for um slot de bloqueio de torneio (isTournamentSlot === true), NUNCA entra na mensalidade
      if (slot.isTournamentSlot === true) {
        return;
      }

      // Filtrar por data do período se disponível
      if (slot.date && (slot.date < pStart || slot.date > pEnd)) {
        return;
      }

      const eligible = isBookingCommissionEligible(slot);
      if (eligible) {
        const slotPrice = Number(slot.price || slot.paidAmount || 0);
        const { rate: frozenRate, amount: commissionAmount } = calculateSlotCommission(slot, court);

        totalEligibleBookingsCount++;
        totalRevenueAmount += slotPrice;
        totalAccumulatedCommission += commissionAmount;

        const det = detailsMap.get(court.id)!;
        det.eligibleBookingsCount++;
        det.revenueAmount += slotPrice;
        det.commissionAmount += commissionAmount;
        det.commissionRate = isCourtFixedMonthly ? 0 : frozenRate; // Preserva taxa congelada ou 0 se Fixed
      } else if (slot.status === "cancelada" || (slot.status as string) === "cancelado") {
        totalCancelledExemptBookingsCount++;
      }
    });
  });

  return {
    periodStart: pStart,
    periodEnd: pEnd,
    nextClosingDate,
    totalEligibleBookingsCount,
    totalRevenueAmount: Number(totalRevenueAmount.toFixed(2)),
    totalAccumulatedCommission: Number(totalAccumulatedCommission.toFixed(2)),
    totalCancelledExemptBookingsCount,
    status: "EM ABERTO",
    detailsByCourt: Array.from(detailsMap.values()),
  };
}

/**
 * Calcula os valores financeiros da reserva incluindo a Taxa de Entrada (se ativa no Super Admin).
 * - grossAmount: valor base da reserva/ingresso (R$)
 * - entryFee: taxa de entrada congelada (R$)
 * - totalAmount: valor total a ser pago pelo atleta (R$)
 */
export function calculateEntryFeeForBooking(basePrice: number): {
  grossAmount: number;
  entryFee: number;
  totalAmount: number;
  entranceFeeEnabled: boolean;
} {
  const sysSettings = getSystemSettings();
  const enabled = Boolean(sysSettings.entranceFeeEnabled);
  const feeVal = enabled ? Number(sysSettings.entranceFeeAmount || 0) : 0;

  const grossAmount = Number((basePrice || 0).toFixed(2));
  const entryFee = Number(feeVal.toFixed(2));
  const totalAmount = Number((grossAmount + entryFee).toFixed(2));

  return {
    grossAmount,
    entryFee,
    totalAmount,
    entranceFeeEnabled: enabled && feeVal > 0,
  };
}

/**
 * Normaliza CPF removendo pontuações e caracteres não numéricos.
 */
export function normalizeCpf(cpf: string): string {
  if (!cpf) return "";
  return cpf.replace(/\D/g, "").trim();
}

/**
 * Normaliza E-mail removendo espaços e convertendo para minúsculas.
 */
export function normalizeEmail(email: string): string {
  if (!email) return "";
  return email.trim().toLowerCase();
}

/**
 * Realiza busca inteligente de clientes por Nome, CPF ou E-mail normalizados.
 */
export function searchClientsIntelligent(
  queryStr: string,
  userProfiles: any[] = [],
  groupReservations: any[] = []
): Array<{ name: string; cpf: string; email: string; phone: string }> {
  if (!queryStr || queryStr.trim().length < 2) return [];

  const rawQuery = queryStr.trim().toLowerCase();
  const digitsQuery = normalizeCpf(queryStr);

  const resultsMap = new Map<string, { name: string; cpf: string; email: string; phone: string }>();

  const addCandidate = (name: string, cpf: string, email: string, phone: string) => {
    if (!name && !email && !cpf) return;
    const normEmail = normalizeEmail(email);
    const normCpf = normalizeCpf(cpf);
    const key = normEmail || normCpf || name.toLowerCase();

    if (!resultsMap.has(key)) {
      resultsMap.set(key, {
        name: name || "Atleta",
        cpf: cpf || "",
        email: normEmail,
        phone: phone || "",
      });
    }
  };

  // 1. Pesquisar na lista de perfis de usuário / atletas
  userProfiles.forEach((u) => {
    const uName = u.name || u.displayName || u.responsibleName || "";
    const uCpf = u.cpf || u.responsibleCpf || "";
    const uEmail = u.email || u.responsibleEmail || "";
    const uPhone = u.phone || u.phoneNumber || u.responsiblePhone || "";

    const nameMatch = uName.toLowerCase().includes(rawQuery);
    const emailMatch = normalizeEmail(uEmail).includes(rawQuery);
    const cpfMatch = digitsQuery.length >= 3 && normalizeCpf(uCpf).includes(digitsQuery);

    if (nameMatch || emailMatch || cpfMatch) {
      addCandidate(uName, uCpf, uEmail, uPhone);
    }
  });

  // 2. Pesquisar no histórico de reservas de Day Use de grupos
  groupReservations.forEach((res) => {
    const rName = res.responsibleName || "";
    const rCpf = res.responsibleCpf || "";
    const rEmail = res.responsibleEmail || "";
    const rPhone = res.responsiblePhone || "";

    const nameMatch = rName.toLowerCase().includes(rawQuery);
    const emailMatch = normalizeEmail(rEmail).includes(rawQuery);
    const cpfMatch = digitsQuery.length >= 3 && normalizeCpf(rCpf).includes(digitsQuery);

    if (nameMatch || emailMatch || cpfMatch) {
      addCandidate(rName, rCpf, rEmail, rPhone);
    }
  });

  return Array.from(resultsMap.values()).slice(0, 8); // Retorna até 8 sugestões
}

export interface TournamentRegistrationFinancialsResult {
  baseAmount: number;
  grossAmount: number;
  unitFee: number;
  feeType: "equipe" | "individual";
  teamSize: number;
  platformFeeRate: number;
  platformFeeAmount: number;
  feeMode: "add" | "absorb";
  feeCommissionMode: "add" | "absorb";
  feeCommissionModeApplied: "add" | "absorb";
  commissionRateApplied: number;
  organizerGrossAmount: number;
  organizerNetAmount: number;
  totalAmountToPay: number;
  asaasFeeAmount?: number;
  transferFeeAmount?: number;
  arenaCommissionRate?: number;
  arenaCommissionAmount?: number;
}

/**
 * Cálculo Financeiro da Inscrição de Torneio.
 * Mantém total separação entre:
 * 1. Taxa da Plataforma (platformFeeRate / platformFeeAmount) - remuneração do sistema.
 * 2. Comissão da Arena (arenaCommissionRate / arenaCommissionAmount) - uso de quadras/espaço.
 * Suporta modelos 'add' (taxa adicionada ao atleta) e 'absorb' (taxa deduzida do organizador).
 */
export function calculateTournamentRegistrationFinancials(
  registrationFee: number,
  commissionRate: number,
  feeCommissionMode: "add" | "absorb" = "add",
  feeType: "equipe" | "individual" = "equipe",
  teamSize: number = 2,
  arenaCommissionRate?: number
): TournamentRegistrationFinancialsResult {
  const effectiveBaseFee = feeType === "individual" ? (registrationFee || 0) * Math.max(1, teamSize) : (registrationFee || 0);
  const grossAmount = Number(Math.max(0, effectiveBaseFee).toFixed(2));
  const platformFeeRate = Number((commissionRate !== undefined && commissionRate !== null ? commissionRate : 4.0).toFixed(2));
  const feeCommissionModeApplied = feeCommissionMode || "add";

  // Se torneio for gratuito (R$ 0,00), a taxa é 0 e todos os totais são 0
  const isFree = grossAmount === 0;
  const platformFeeAmount = isFree ? 0 : Number(((grossAmount * platformFeeRate) / 100).toFixed(2));

  // Eventual comissão da arena (separada da taxa da plataforma)
  const effectiveArenaRate = arenaCommissionRate !== undefined && arenaCommissionRate !== null ? Number(arenaCommissionRate) : 0;
  const arenaCommissionAmount = isFree || effectiveArenaRate <= 0
    ? 0
    : Number(((grossAmount * effectiveArenaRate) / 100).toFixed(2));

  let organizerNetAmount = grossAmount;
  let totalAmountToPay = grossAmount;

  if (isFree) {
    totalAmountToPay = 0;
    organizerNetAmount = 0;
  } else if (feeCommissionModeApplied === "add") {
    totalAmountToPay = Number((grossAmount + platformFeeAmount).toFixed(2));
    organizerNetAmount = grossAmount;
  } else {
    // absorb
    totalAmountToPay = grossAmount;
    organizerNetAmount = Number(Math.max(0, grossAmount - platformFeeAmount).toFixed(2));
  }

  return {
    baseAmount: grossAmount,
    grossAmount,
    unitFee: registrationFee || 0,
    feeType,
    teamSize,
    platformFeeRate,
    platformFeeAmount,
    feeMode: feeCommissionModeApplied,
    feeCommissionMode: feeCommissionModeApplied,
    feeCommissionModeApplied,
    commissionRateApplied: platformFeeRate,
    organizerGrossAmount: grossAmount,
    organizerNetAmount,
    totalAmountToPay,
    asaasFeeAmount: undefined, // To be populated when Asaas API provides the netValue
    transferFeeAmount: undefined, // To be populated when the transfer cost is known
    arenaCommissionRate: effectiveArenaRate > 0 ? effectiveArenaRate : undefined,
    arenaCommissionAmount: arenaCommissionAmount > 0 ? arenaCommissionAmount : undefined,
  };
}

/**
 * Confirmação Idempotente de Inscrição em Torneio (ETAPA 7C-2).
 * Verifica se o paymentId informado já foi processado/pago na lista de inscrições.
 */
export function confirmTournamentRegistrationIdempotent(
  currentRegistrations: any[] = [],
  newRegistration: any
): {
  registrations: any[];
  isAlreadyProcessed: boolean;
  confirmedRegistration: any;
} {
  if (newRegistration.paymentId) {
    const existing = currentRegistrations.find(
      (item) => item.paymentId === newRegistration.paymentId && item.paid === true
    );
    if (existing) {
      return {
        registrations: currentRegistrations,
        isAlreadyProcessed: true,
        confirmedRegistration: existing,
      };
    }
  }

  // Garantir congelamento dos campos financeiros se ainda não preenchidos
  const confirmedRegistration = {
    ...newRegistration,
    paid: true,
    paidAt: newRegistration.paidAt || new Date().toISOString(),
    referenceType: "tournament",
  };

  return {
    registrations: [...currentRegistrations, confirmedRegistration],
    isAlreadyProcessed: false,
    confirmedRegistration,
  };
}

/**
 * Resolução Segura do Destinatário e Chave PIX do Organizador de Torneio (ETAPA 7C-2).
 * NUNCA utiliza o dono da arena ou arenaPixKey como destinatário de repasse de torneio.
 */
export function resolveTournamentOrganizerPayoutRecipient(
  tournamentData: {
    organizerUserId?: string;
    organizerCpf?: string;
    organizerEmail?: string;
    pixKeyOrganizer?: string;
    arenaOwnerId?: string;
    ownerId?: string;
  },
  organizerProfile?: {
    id?: string;
    pixKey?: string;
    pixKeyType?: string;
  }
): {
  organizerUserId: string | null;
  pixKey: string;
  isValidRecipient: boolean;
  status: "OK" | "MISSING_ORGANIZER_ID" | "MISSING_PIX_KEY";
  reason?: string;
} {
  const organizerUserId = tournamentData.organizerUserId || organizerProfile?.id || null;

  if (!organizerUserId) {
    return {
      organizerUserId: null,
      pixKey: "",
      isValidRecipient: false,
      status: "MISSING_ORGANIZER_ID",
      reason: "ID do organizador do torneio não especificado",
    };
  }

  // Prioridade de Chave PIX do Organizador:
  // 1. pixKeyOrganizer configurado diretamente no torneio
  // 2. pixKey do perfil do organizador
  const resolvedPixKey = (tournamentData.pixKeyOrganizer || organizerProfile?.pixKey || "").trim();

  if (!resolvedPixKey || resolvedPixKey.length < 3) {
    return {
      organizerUserId,
      pixKey: "",
      isValidRecipient: false,
      status: "MISSING_PIX_KEY",
      reason: "Chave PIX do organizador do torneio não cadastrada",
    };
  }

  return {
    organizerUserId,
    pixKey: resolvedPixKey,
    isValidRecipient: true,
    status: "OK",
  };
}


